import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthUser } from '../types/auth';

/**
 * Single source of truth for the SPA's auth session.
 *
 * Before 2026-05-25 the access token, refresh token, and role were
 * also written to flat localStorage keys consumed by the axios
 * interceptor. That split created drift: a partial logout (or a
 * mid-flow tab close) could leave one store cleared while the other
 * held a valid token, opening a window where XSS payloads could
 * still exfiltrate a "logged-out" credential. The defensive shim at
 * `auth.service.ts:80-89` existed specifically to paper over that
 * drift. With this store as the sole authority, both the React tree
 * and the axios interceptor read from `useAuthStore.getState()` —
 * one write path, one clear path, no drift possible.
 *
 * Persisted to `localStorage["auth-storage"]` by zustand's persist
 * middleware. The middleware writes through synchronously, so any
 * code reading `getState()` immediately after `setSession()` sees
 * the new value AND the next page reload starts in the same state.
 */
export interface AuthSessionInput {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  /** Replace the entire session in one atomic update. Used by every
   *  auth flow that produces a fresh JWT pair (password login, OAuth
   *  exchange, token refresh, accept-invitation). */
  setSession: (input: AuthSessionInput) => void;
  /** Update just the user profile without touching the tokens. Used
   *  after PATCH /auth/me or password rotation so the topbar/profile
   *  reflect the latest state without forcing a new login round-trip. */
  setUser: (user: AuthUser) => void;
  /** Tear down the session atomically. The persist middleware writes
   *  the cleared state to localStorage in the same tick, so a hard
   *  navigation right after this call lands on /login with no token
   *  residue. */
  logout: () => void;
}

const STORAGE_KEY = 'auth-storage';

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      setSession: ({ accessToken, refreshToken, user }: AuthSessionInput): void =>
        set({ accessToken, refreshToken, user, isAuthenticated: true }),
      setUser: (user: AuthUser): void => set({ user }),
      logout: (): void =>
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
        }),
    }),
    { name: STORAGE_KEY }
  )
);

/**
 * Sync read of the access token straight from the persist envelope.
 *
 * The axios request interceptor fires on the very first API call,
 * which can land BEFORE zustand's persist middleware has finished
 * hydrating (hydration runs in a microtask even for localStorage in
 * v4.5+). Reading `useAuthStore.getState().accessToken` at that
 * moment returns the initial `null`, so the request goes out with no
 * Authorization header and the server 403s.
 *
 * This helper reads the same persisted envelope the middleware
 * writes, so it returns the hydrated value synchronously. There's
 * still ONE source of truth (`localStorage["auth-storage"]`), just
 * two reader paths — the React side uses the live store (which
 * stays correct after the microtask), the axios interceptor uses
 * this sync read (which is correct from the first call).
 */
export function readPersistedAccessToken(): string | null {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { accessToken?: string | null } };
    return parsed.state?.accessToken ?? null;
  } catch {
    return null;
  }
}

/** Sync read of the refresh token from the persist envelope. Same
 *  motivation as `readPersistedAccessToken` — the axios 401-retry
 *  path needs the refresh token before zustand hydration completes. */
export function readPersistedRefreshToken(): string | null {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { refreshToken?: string | null } };
    return parsed.state?.refreshToken ?? null;
  } catch {
    return null;
  }
}
